import { ChangeDetectorRef, Component, Inject, NgZone, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { BookingOrder, BookingPayload, Doctor, DoctorService, PricingLineItem, Slot, SlotDay } from '../../../core/services/doctor.service';
import { catchError, EMPTY, switchMap } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';

type Step = 'details' | 'billing' | 'confirmed';
type Tab = 'overview' | 'slots';

@Component({
  selector: 'app-doctor-dialog',
  imports: [CommonModule, FormsModule],
  templateUrl: './doctor-dialog.component.html',
  styleUrl: './doctor-dialog.component.css',
})
export class DoctorDialogComponent implements OnInit {
  doctor: Doctor;
  step: Step = 'details';
  activeTab: Tab = 'overview';

  // slots
  slotDays: SlotDay[] = [];
  selectedDayIdx = 0;
  groups: { title: string; slots: Slot[] }[] = [];
  selectedSlot: Slot | null = null;
  loadingSlots = false;
  slotsLoaded = false;
  slotsError = false;

  // billing
  pricing: any;
  pricingError = false;
  coupon = '';
  couponMsg = '';
  submitted = false;
  paying = false;
  // form = {
  //   first_name: localStorage.getItem('first_name') || '',
  //   last_name: localStorage.getItem('last_name') || '',
  //   phone: localStorage.getItem('phone') || '',      // TODO: confirm where phone/email live in your app
  //   email: localStorage.getItem('email') || '',
  //   country: 'India',
  //   booking_for: 'Myself',
  //   reason: '',
  // };
  form = {
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    country: '',
    booking_for: 'Myself',
    reason: '',
  };

  profileLoaded = false;
  profileError = false;
  profileData: any[] = [];

  // confirmation
  confirmation: { booking_id: any; amount_paid: number } | null = null;

  private readonly widths: Record<Step, string> = { details: '860px', billing: '1040px', confirmed: '600px' };

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { doctor: Doctor },
    private ref: MatDialogRef<DoctorDialogComponent>,
    private svc: DoctorService,
    private toastr: ToastrService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private auth: AuthService
  ) {
    this.doctor = data.doctor;
  }

  ngOnInit(): void {
    console.log("inside the dialog")
    this.ref.disableClose = false;
    this.getProfile();
  }

  /* ── Step / tab control ─────────────────────────────────────────────── */
  private setStep(step: Step): void {
    this.step = step;
    this.ref.updateSize(this.widths[step]);
  }
    
getProfile(): void {
  this.profileError = false;
  this.auth.get_digicoffer_profile().subscribe({
    next: (res: any) => {
      const p = res?.data?.profile;
      if (!p) { this.profileError = true; return; }

      // primary citizen entry holds the country
      const primary = p.citizen?.find((c: any) => c.index === 'citizen_primary') || p.citizen?.[0];

      this.form = {
        ...this.form,                       // keep booking_for / reason as the user set them
        first_name: p.first_name || '',
        last_name:  p.last_name  || '',
        phone:      p.mobile || primary?.mobile_phone || '',
        email:      p.email  || '',
        country:    primary?.country || '',
      };
      this.profileLoaded = true;
    },
    error: () => { this.profileError = true; },
  });
}

  openTab(tab: Tab): void {
    this.activeTab = tab;
    if (tab === 'slots' && !this.slotsLoaded) this.loadSlots();
  }

  close(): void {
    if (this.paying) return;
    this.ref.close({ booked: this.step === 'confirmed' });
  }

  /* ── Slots ──────────────────────────────────────────────────────────── */
  /** Slots already came with the doctor lookup — no extra API call */
  loadSlots(): void {
    this.slotDays = this.doctor.days || [];
    this.slotsLoaded = true;
    const first = this.slotDays.findIndex(d => d.slots.some(s => s.available));
    this.selectDay(first >= 0 ? first : 0);
  }

  selectDay(i: number): void {
    this.selectedDayIdx = i;
    const slots = this.slotDays[i]?.slots || [];
    this.groups = (['Morning', 'Afternoon', 'Evening'] as const)
      .map(title => ({ title, slots: slots.filter(s => s.period === title) }))
      .filter(g => g.slots.length > 0);
  }

  availableCount(d: SlotDay): number { return d.slots.filter(s => s.available).length; }

  dayLabel(d: SlotDay, i: number): string { return d.label || ''; }   // '' → template falls back to the date pipe

  /** Start Date of the selected slot (day date + "05:00 PM") */
  slotStart(): Date {
    const dt = new Date((this.slotDays[this.selectedDayIdx]?.date || '') + 'T00:00:00');
    const m = /(\d+):(\d+)\s*(AM|PM)/i.exec(this.selectedSlot?.label || '');
    if (m) { let h = +m[1] % 12; if (m[3].toUpperCase() === 'PM') h += 12; dt.setHours(h, +m[2]); }
    return dt;
  }

  /** Clicking a slot opens the billing step */
  selectSlot(slot: Slot): void {
    this.selectedSlot = slot;
    this.submitted = false;
    this.setStep('billing');
    this.loadPricing();
  }

  backToSlots(): void {
    if (this.paying) return;
    this.activeTab = 'slots';
    this.setStep('details');
  }

  /* ── Billing ────────────────────────────────────────────────────────── */
 loadPricing(applyingCoupon = false): void {
  if (!this.selectedSlot) return;
  this.pricingError = false;
  this.svc.getPricing(this.doctor.doctor_code, this.doctor.consultation_fee, this.selectedSlot.id, this.coupon.trim() || undefined).subscribe({
    next: res => {
      this.pricing = res?.line_items ? res : null;
      this.pricingError = !this.pricing;
      if (applyingCoupon) {
    const d = this.pricing?.line_items.find((li: PricingLineItem) => li.type === 'discount');
    this.couponMsg = d ? `${d.label} applied` : 'Coupon applied';
  }
      this.cdr.markForCheck();
    },
    error: err => {
      if (applyingCoupon) { this.couponMsg = err?.error?.msg || 'Invalid coupon code'; }
      else { this.pricingError = true; }
      this.cdr.markForCheck();
    },
  });
}

  applyCoupon(): void {
    if (!this.coupon.trim()) return;
    this.loadPricing(true);
  }

  get formValid(): boolean {
    return !!this.form.first_name.trim() && !!this.form.last_name.trim();
  }


bookSlot(): void {
  this.submitted = true;

  if (!this.formValid || !this.pricing || !this.selectedSlot || this.paying) {
    return;
  }

  // Ensure valid pricing before creating appointment
  const finalAmount = Number(this.pricing?.totals?.final_amount);

  if (!finalAmount || finalAmount <= 0 || this.pricingError) {
    this.toastr.error(
      'Price details could not be loaded. Please retry before booking.'
    );
    return;
  }

  this.paying = true;

  const appointmentPayload = {
    lawyer_id: this.doctor.id,
    slot_id: this.selectedSlot.id
  };

  const userId =
    localStorage.getItem('PK') || localStorage.getItem('user_id');

  if (!userId) {
    this.paying = false;
    this.toastr.error('Please login to continue.');
    return;
  }

  this.svc.createAppointment(appointmentPayload)
    .pipe(
      switchMap((appointmentRes: any) => {

        console.log('Appointment created:', appointmentRes);

        if (appointmentRes?.error) {
          this.toastr.error(appointmentRes.msg || 'Slot unavailable.');
          return EMPTY;
        }

        const appointmentId =
          appointmentRes?.appointment_id || appointmentRes?.id;

        if (!appointmentId) {
          this.toastr.error('Appointment ID was not returned.');
          return EMPTY;
        }

        localStorage.setItem('appointment_id', appointmentId);

        // Create payment order
        const paymentPayload: BookingPayload = {
          user_id: userId,
          appointment_id: appointmentId,
          lawyer_id: this.doctor?.id,
          amount: finalAmount,
          slot_id: this.selectedSlot?.id
        };

        this.toastr.info('Initiating payment...');

        return this.svc.createBooking(paymentPayload);
      }),

      catchError((err) => {
        console.error('Booking/payment error:', err);

        this.toastr.error(
          err?.error?.msg ||
          err?.error?.message ||
          'Something went wrong. Please try again.'
        );

        this.paying = false;
        return EMPTY;
      })
    )
    .subscribe({
      next: async (res: any) => {

        console.log('Payment order created:', res);

        const order = res?.data;

        if (!order?.order_id || !order?.checkout_options?.key) {
          this.paying = false;
          this.toastr.error('Invalid payment order response.');
          return;
        }

        const loaded = await this.svc.loadRazorpay();

        this.zone.run(() => {
          if (!loaded) {
            this.paying = false;
            this.toastr.error('Unable to load Razorpay. Please try again.');
            return;
          }

          this.openCheckout(order);
        });
      }
    });
}

  private openCheckout(order:any): void {

  const options = {
    ...order.checkout_options,

    key: order.checkout_options.key,
    order_id: order.order_id,

    description: `Consultation with ${this.doctor.name}`,

    handler: (response: any) => {
      this.zone.run(() => {
        this.verify(order, response);
      });
    },

    modal: {
      ondismiss: () => {
        this.zone.run(() => {
          this.paying = false;
          this.toastr.info('Payment cancelled.');
        });
      }
    }
  };

  const rzp = new (window as any).Razorpay(options);

  rzp.on('payment.failed', (e: any) => {
    this.zone.run(() => {
      this.paying = false;
      this.toastr.error(
        e?.error?.description || 'Payment failed. Please try again.'
      );
    });
  });

  rzp.open();
}

 private verify(order: any, r: any): void {

  this.svc.verifyPayment({
    razorpay_order_id: r.razorpay_order_id,
    razorpay_payment_id: r.razorpay_payment_id,
    razorpay_signature: r.razorpay_signature
  }).subscribe({

    next: (res: any) => {
      this.paying = false;

      // if (!res?.status) {
      //   this.toastr.error('Payment verification failed.');
      //   return;
      // }

      this.confirmation = {
        booking_id: localStorage.getItem('appointment_id'),
        amount_paid: this.pricing?.totals?.final_amount ?? 0
      };

      //this.toastr.success('Appointment booked successfully.');

      this.setStep('confirmed');
      this.cdr.markForCheck();
    },

    error: (err) => {
      this.paying = false;

      // this.toastr.error(
      //   `Payment received but verification failed. Please contact support with payment ID ${r?.razorpay_payment_id || ''}.`
      // );
    }
  });
}

  /* ── Confirmation ───────────────────────────────────────────────────── */
  addToCalendar(): void {
    if (!this.selectedSlot) return;
    const start = this.slotStart();
    const end = new Date(start.getTime() + 30 * 60000);
    const f = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//VitaGist//Appointments//EN', 'BEGIN:VEVENT',
      `UID:${this.confirmation?.booking_id}@vitagist`,
      `DTSTAMP:${f(new Date())}`, `DTSTART:${f(start)}`, `DTEND:${f(end)}`,
      `SUMMARY:Appointment with ${this.doctor.name}`,
      `LOCATION:${this.doctor.hospital}`,
      `DESCRIPTION:Booking ID ${this.confirmation?.booking_id}`,
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
    a.download = `appointment-${this.confirmation?.booking_id}.ics`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  initials(name: string): string {
    const parts = (name || '').replace(/^dr\.?\s+/i, '').trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  }
}